import { DocsSection, DocsPage, FlatPage } from "features/Docs/types/docs.types"

/**
 * The documentation navigation tree — the single source of truth for
 * sections, slugs, titles, and order. Content authors mirror these titles
 * in each markdown file's frontmatter; the `file` key of every page matches
 * the `import.meta.glob` key in `content/index.ts`.
 */
export const DOCS_SECTIONS: DocsSection[] = [
  {
    slug: "getting-started",
    title: "Getting Started",
    pages: [{ slug: "welcome", title: "Welcome to DATA(Q)", file: "./getting-started/welcome.md" }],
  },
  {
    slug: "format-guide",
    title: "Format Guide",
    pages: [
      { slug: "overview", title: "Starting a Questionnaire", file: "./format-guide/overview.md", group: "Basics" },
      { slug: "text-and-notes", title: "Text and Notes", file: "./format-guide/text-and-notes.md", group: "Basics" },
      { slug: "questions-select", title: "Select Questions", file: "./format-guide/questions-select.md", group: "Questions" },
      { slug: "questions-open-ended", title: "Open-Ended Questions", file: "./format-guide/questions-open-ended.md", group: "Questions" },
      { slug: "grids", title: "Grids and Numeric Grids", file: "./format-guide/grids.md", group: "Questions" },
      { slug: "lists", title: "Lists", file: "./format-guide/lists.md", group: "Lists" },
      { slug: "list-features", title: "List Features", file: "./format-guide/list-features.md", group: "Lists" },
      { slug: "ordered-lists-and-ranges", title: "Ordered Lists and Ranges", file: "./format-guide/ordered-lists-and-ranges.md", group: "Lists" },
      { slug: "exceptions", title: "Exceptions", file: "./format-guide/exceptions.md", group: "Lists" },
      { slug: "labels", title: "Labels", file: "./format-guide/labels.md", group: "Lists" },
      { slug: "routing", title: "Routing and Logic", file: "./format-guide/routing.md", group: "Logic & Routing" },
      { slug: "conditional-content", title: "Conditional Content", file: "./format-guide/conditional-content.md", group: "Logic & Routing" },
      { slug: "recode", title: "RECODE: Deriving Answers", file: "./format-guide/recode.md", group: "Logic & Routing" },
      { slug: "properties", title: "Question Properties", file: "./format-guide/properties.md", group: "Properties" },
      { slug: "validation", title: "Validation Properties", file: "./format-guide/validation.md", group: "Properties" },
      { slug: "loops", title: "FOR Loops", file: "./format-guide/loops.md", group: "Advanced" },
      { slug: "piping", title: "Text Piping", file: "./format-guide/piping.md", group: "Advanced" },
      { slug: "hidden-questions", title: "Hidden Questions", file: "./format-guide/hidden-questions.md", group: "Advanced" },
      { slug: "variants", title: "Questionnaire Variants", file: "./format-guide/variants.md", group: "Advanced" },
      { slug: "data-and-aliases", title: "Capturing Data & Keyword Aliases", file: "./format-guide/data-and-aliases.md", group: "Advanced" },
      { slug: "full-example", title: "Full Example Questionnaire", file: "./format-guide/full-example.md", group: "Example" },
    ],
  },
  {
    slug: "query-language",
    title: "Query Language",
    pages: [
      { slug: "overview", title: "Query Language Overview", file: "./query-language/overview.md", group: "Fundamentals" },
      { slug: "data-model", title: "The Data Model", file: "./query-language/data-model.md", group: "Fundamentals" },
      { slug: "inspecting-data", title: "Inspecting Data", file: "./query-language/inspecting-data.md", group: "Fundamentals" },
      { slug: "crosstabs-xtab", title: "Crosstabs (XTAB)", file: "./query-language/crosstabs-xtab.md", group: "Crosstabs & Output" },
      { slug: "tables-dtab", title: "Data Tables (DTAB)", file: "./query-language/tables-dtab.md", group: "Crosstabs & Output" },
      { slug: "output-and-export", title: "Output and Export", file: "./query-language/output-and-export.md", group: "Crosstabs & Output" },
      { slug: "structure-commands", title: "Structure Commands", file: "./query-language/structure-commands.md", group: "Transforming Data" },
      { slug: "field-operations", title: "Field Operations", file: "./query-language/field-operations.md", group: "Transforming Data" },
      { slug: "filters-and-conditions", title: "Filters and Conditions", file: "./query-language/filters-and-conditions.md", group: "Transforming Data" },
      { slug: "calc-and-cat", title: "CALC and CAT", file: "./query-language/calc-and-cat.md", group: "Transforming Data" },
      { slug: "session-and-workspace", title: "Session and Workspace", file: "./query-language/session-and-workspace.md", group: "Session & Weighting" },
      { slug: "weighting", title: "Weighting", file: "./query-language/weighting.md", group: "Session & Weighting" },
    ],
  },
  {
    slug: "best-practices",
    title: "Best Practices",
    pages: [
      { slug: "questionnaire-best-practices", title: "Questionnaire Best Practices", file: "./best-practices/questionnaire-best-practices.md" },
      { slug: "query-gotchas-and-missing-values", title: "Query Gotchas and Missing Values", file: "./best-practices/query-gotchas-and-missing-values.md" },
    ],
  },
]

/** Look up a section by slug. */
export const findSection = (sectionSlug: string): DocsSection | undefined =>
  DOCS_SECTIONS.find((section) => section.slug === sectionSlug)

/** Look up a page by section + page slug. */
export const findPage = (
  sectionSlug: string,
  pageSlug: string,
): { section: DocsSection; page: DocsPage } | undefined => {
  const section = findSection(sectionSlug)
  const page = section?.pages.find((p) => p.slug === pageSlug)
  if (!section || !page) return undefined
  return { section, page }
}

/** Build a page's route path from its section and slug. */
export const pageRoute = (sectionSlug: string, pageSlug: string): string =>
  `/docs/${sectionSlug}/${pageSlug}`

/** All pages flattened into reading order, with route + index attached. */
export const flattenPages = (): FlatPage[] => {
  const flat: FlatPage[] = []
  DOCS_SECTIONS.forEach((section) => {
    section.pages.forEach((page) => {
      flat.push({
        section,
        page,
        route: pageRoute(section.slug, page.slug),
        index: flat.length,
      })
    })
  })
  return flat
}
